import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import FormTextField from './FormTextField';

import FieldGroup from './FieldGroup';
import messages from './EmailContainer.messages';

import { useIntl } from 'react-intl';
import { usePrevious } from '../utilities/hooks';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/glyph/edit';

const IconButtonWrapper = styled.div`
  padding: 3px;
  margin-left: -1px;
`;

export enum FieldViewType {
  Editable = 'editable',
  ReadOnlyWithEditButton = 'read-only-with-edit-button',
  Restricted = 'restricted',
}

export interface EmailProps {
  fieldViewType: FieldViewType;
  fieldError?: string;
  onChange: (value: string) => void;
  onEmailClick: () => void;
  value: string;
  placeholderOverride?: string;
  inputRef: React.RefObject<HTMLInputElement>;
}

const EmailContainer = ({
  fieldError,
  fieldViewType,
  onChange,
  onEmailClick,
  placeholderOverride,
  value,
  inputRef,
}: EmailProps) => {
  const { formatMessage } = useIntl();

  const isEditable = fieldViewType === FieldViewType.Editable;
  const prevIsReadOnly = usePrevious(!isEditable);

  useEffect(() => {
    if (isEditable && prevIsReadOnly) {
      inputRef.current?.focus();
    }
  }, [isEditable, prevIsReadOnly, inputRef]);

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    onChange(newValue);
  };

  const placeholder = placeholderOverride || formatMessage(messages.loginFormUsernamePlaceholder);
  return (
    <FieldGroup>
      <FormTextField
        testId="username" // used in pollinators
        name="username"
        autoComplete="username"
        type="email"
        placeholder={placeholder}
        label={formatMessage(messages.loginFormUsernameLabel)}
        autoFocus={isEditable}
        onChange={handleChange}
        isInvalid={!!fieldError}
        invalidMessage={fieldError}
        value={value}
        ref={inputRef}
        isRequired
        isDisabled={!isEditable}
        elemAfterInput={
          fieldViewType === FieldViewType.ReadOnlyWithEditButton && (
            <IconButtonWrapper>
              <IconButton
                icon={EditIcon}
                label={formatMessage(messages.loginFormReadOnlyUsernameAriaLabel)}
                onClick={onEmailClick}
                appearance="subtle"
              />
            </IconButtonWrapper>
          )
        }
      />
    </FieldGroup>
  );
};

export default EmailContainer;
